import { lazy, Suspense, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import HomePage from "@/pages/HomePage";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";

// Lazy-loaded page components — only fetched when the route is visited
const BarcodeScanPage = lazy(() => import("@/pages/BarcodeScanPage"));
const UnifiedSearchPage = lazy(() => import("@/pages/UnifiedSearchPage"));
const ReportPage = lazy(() => import("@/pages/report"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const RepositoryPage = lazy(() => import("@/pages/RepositoryPage"));
const ContributePage = lazy(() => import("@/pages/ContributePage"));
const DonatePage = lazy(() => import("@/pages/DonatePage"));
const DonateSuccessPage = lazy(() => import("@/pages/DonateSuccessPage"));
const DonateCancelPage = lazy(() => import("@/pages/DonateCancelPage"));
const SourcesPage = lazy(() => import("@/pages/SourcesPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const JournalPage = lazy(() => import("@/pages/JournalPage"));
const PersonalizationPage = lazy(() => import("@/pages/PersonalizationPage"));

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ textAlign: "center", padding: "4rem 1rem" }}>
          <h2>Something went wrong.</h2>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1.5rem",
              cursor: "pointer",
            }}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <div className="fli-app min-h-screen flex flex-col">
      <div className="grain-overlay" />
      <BrowserRouter>
        <ScrollToTop />
        <Navigation />
        <div className="flex-1">
          <ErrorBoundary>
            <Suspense
              fallback={
                <div style={{ textAlign: "center", padding: "4rem 1rem" }}>
                  <div className="loading-spinner" />
                  <p>Loading...</p>
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/scan" element={<BarcodeScanPage />} />
                <Route path="/search" element={<UnifiedSearchPage />} />
                {/* Redirect old routes to unified search */}
                <Route path="/generate" element={<Navigate to="/search" replace />} />
                <Route path="/report/:barcode" element={<ReportPage />} />
                <Route path="/repository" element={<RepositoryPage />} />
                <Route path="/contribute" element={<ContributePage />} />
                <Route path="/sources" element={<SourcesPage />} />
                <Route path="/donate" element={<DonatePage />} />
                <Route path="/donate/success" element={<DonateSuccessPage />} />
                <Route path="/donate/cancel" element={<DonateCancelPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/journal" element={<JournalPage />} />
                <Route path="/personalize" element={<PersonalizationPage />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </div>
        <Footer />
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
